import moment from "moment";
import { toast } from "react-toastify";

import { BASE_URL, IMAGE_URL } from "@/configs";
import { translate } from "@/lib/i18n";

export * from "./passwordPolicy";

export const getMediaUrl = (file?: string): string => {
  if (!file) return "";
  if (/^https?:\/\//i.test(file)) return file;
  // The nav icons ship in this app's own public folder, not on the API.
  if (file.startsWith("/assets/")) return file;
  if (file.startsWith("/")) return `${BASE_URL}${file}`;
  return `${IMAGE_URL}/${file}`;
};

export const handleError = (err: unknown, defaultMessage?: string) => {
  if (err instanceof Error) {
    if ((err as { silent?: boolean }).silent) return;
    // Same text, one toast: a page firing several requests during an outage would stack them.
    toast(err.message, { type: "error", toastId: err.message });
  } else {
    toast(defaultMessage || translate("common.unknownError"), { type: "error" });
  }
};

export const dateFormat = (value: string | number | Date): string => {
  return moment(value).format("YYYY-MM-DD");
};

export const dateTimeFormat = (value: string | number | Date): string => {
  return moment(value).format("YYYY-MM-DD HH:mm:ss");
};

// A picked date as the YYYY-MM-DD calendar day the list APIs expect.
export const toApiDate = (value: Date | null): string =>
  value ? moment(value).format("YYYY-MM-DD") : "";

// Plain number with thousands separators, e.g. 200,000,000.
export const formatNumber = (value: number | null | undefined): string =>
  Number(value ?? 0).toLocaleString("en-US", { maximumFractionDigits: 2 });

export const formatBalance = (amount: number, currency: string): string => {
  // A row saved without a currency is in USD, the platform base.
  const code = currency || "USD";
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: code,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    // Non-ISO codes (e.g. USDT) land here; never let a missing amount crash the page.
    return `${Number(amount ?? 0).toLocaleString("en-US", { maximumFractionDigits: 2 })} ${code}`;
  }
};
